import { clipboardConfig } from "./config";

/**
 * Where the latest copy waits for a reload and for tabs opened later: one record per person and organisation, so a
 * copy never reaches another person or another organisation's drawings. IndexedDB, per origin, in the browser; a map
 * in tests.
 */

/** A copy as kept. */
export interface KeptCopy {
  /** `keyOf(user, organisation)`. */
  readonly key: string;
  readonly user: string;
  /** The engine's share form, as the worker posted it. */
  readonly payload: string;
  /** What the copy put on the system clipboard. */
  readonly text: string;
  /** Whether that text reached the system clipboard. */
  readonly onSystem: boolean;
  /** When it was copied, in ms since the epoch: the latest copy wins. */
  readonly stamp: number;
}

export interface CopyStore {
  get(key: string): Promise<KeptCopy | undefined>;
  put(copy: KeptCopy): Promise<void>;
  /** Forgets every copy (sign-out, another person). */
  clear(): Promise<void>;
  /** Forgets every copy but `user`'s (another person signed in since they were kept). */
  keepOnly(user: string): Promise<void>;
}

/** The record key of a person's copies in one organisation. */
export function keyOf(user: string, organisation: string): string {
  return JSON.stringify([user, organisation]);
}

function isKept(value: unknown): value is KeptCopy {
  return (
    typeof value === "object" &&
    value !== null &&
    "key" in value &&
    typeof value.key === "string" &&
    "user" in value &&
    typeof value.user === "string" &&
    "payload" in value &&
    typeof value.payload === "string" &&
    "text" in value &&
    typeof value.text === "string" &&
    "onSystem" in value &&
    typeof value.onSystem === "boolean" &&
    "stamp" in value &&
    typeof value.stamp === "number"
  );
}

function request<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error("IndexedDB failed"));
  });
}

/** The browser's store, opened on first use. */
export function indexedDbCopies(): CopyStore {
  let database: Promise<IDBDatabase> | undefined;
  const db = (): Promise<IDBDatabase> => {
    database ??= (() => {
      const req = indexedDB.open(clipboardConfig.database, clipboardConfig.databaseVersion);
      req.onupgradeneeded = () =>
        req.result.createObjectStore(clipboardConfig.store, { keyPath: "key" });
      return request(req);
    })();
    return database;
  };
  const store = async (mode: IDBTransactionMode): Promise<IDBObjectStore> =>
    (await db()).transaction(clipboardConfig.store, mode).objectStore(clipboardConfig.store);
  return {
    async get(key) {
      const value: unknown = await request((await store("readonly")).get(key));
      return isKept(value) ? value : undefined;
    },
    async put(copy) {
      await request((await store("readwrite")).put(copy));
    },
    async clear() {
      await request((await store("readwrite")).clear());
    },
    async keepOnly(user) {
      const all: unknown[] = await request((await store("readonly")).getAll());
      const others = all.filter((value) => !isKept(value) || value.user !== user);
      if (others.length === 0) return;
      const writing = await store("readwrite");
      await Promise.all(
        others.map((value) =>
          isKept(value) ? request(writing.delete(value.key)) : Promise.resolve(undefined),
        ),
      );
    },
  };
}

/** A store in memory, for tests and where IndexedDB is missing. */
export function memoryCopies(): CopyStore & { readonly kept: Map<string, KeptCopy> } {
  const kept = new Map<string, KeptCopy>();
  return {
    kept,
    get: (key) => Promise.resolve(kept.get(key)),
    put: (copy) => {
      kept.set(copy.key, copy);
      return Promise.resolve();
    },
    clear: () => {
      kept.clear();
      return Promise.resolve();
    },
    keepOnly: (user) => {
      for (const [key, copy] of kept) if (copy.user !== user) kept.delete(key);
      return Promise.resolve();
    },
  };
}
